// Centralized copy & business constants for easy editing.

export const BUSINESS = {
  name: "Maui StarPower Network",
  wordmark: "StarPower.Network",
  tagline: "Stop Overpaying HECO. Start Keeping Your Money.",
  phone: "808-707-8412",
  phoneHref: "tel:+18087078412",
  email: "maui@starpower.network",
  calendarSrc: "https://api.leadconnectorhq.com/widget/booking/yBnwWUX4yAfnisr2J4Vd",
  calendarScript: "https://link.msgsndr.com/js/embed.js",
};

export const TRUST_BAR = [
  "Maui-Licensed & Insured",
  "Local Crew",
  "HECO Compliant",
  "5-Star Google Rated",
];

// Hero "snapshot" rows (truthful framing of the offer).
export const HERO_SNAPSHOT = [
  { big: "$0/mo", small: "no monthly payments during the term (Home Equity Agreement option)" },
  { big: "500", small: "minimum ~FICO \u00b7 no income requirement (HEA option)" },
  { big: "10 min", small: "no-pressure discovery call" },
];

export const STAT_CARDS = [
  {
    value: "$0",
    countTo: 0,
    prefix: "$",
    label: "Monthly Payments (HEA Option)",
    sub: "No monthly payments during the term. You settle when you sell, refinance, or at year 10.",
  },
  {
    value: "80\u201390%",
    countTo: 90,
    suffix: "%",
    label: "Potential HECO Bill Reduction",
    sub: "Many Maui homeowners dramatically lower their electricity costs with solar. Savings vary by property.",
  },
  {
    value: "Up to 10 Yrs",
    countTo: 10,
    prefix: "Up to ",
    suffix: " Yrs",
    label: "Home Equity Agreement Term",
    sub: "No monthly payments during the term \u2014 and no prepayment penalty if you settle early.",
  },
];

// Two financing paths (HEA + conventional credit union programs).
export const FINANCING = [
  {
    name: "Home Equity Agreement",
    badge: "No Monthly Payments",
    accent: "#27AE60",
    partner: "Offered through Unlock \u2014 available in Hawaii",
    points: [
      "No monthly payments during the term \u2014 your solar savings stay in your pocket",
      "Qualify on your equity, not your income \u2014 minimum ~500 FICO, no income requirement",
      "Access up to $500,000 from your home\u2019s equity",
      "You keep full title and ownership of your home",
      "Settle when you sell, refinance, or buy out \u2014 up to 10 years, no prepayment penalty",
    ],
    note: "A Home Equity Agreement is not a loan, HELOC, or refinance. An origination fee, third-party closing costs, and an agreed share of your home\u2019s future value (capped by a 19.9% annualized cost limit) apply at settlement. Illustrative only \u2014 your offer may vary.",
  },
  {
    name: "Conventional Credit Union Program",
    badge: "Traditional Financing",
    accent: "#F5A623",
    partner: "Through trusted local credit unions",
    points: [
      "A conventional solar loan with competitive, predictable terms",
      "Fixed monthly payments you can plan around",
      "Own your solar system outright",
      "A great fit if you have strong credit and prefer a classic loan",
      "Local credit union relationships, handled start to finish",
    ],
    note: "Loan rates, terms, and approval are set by the participating credit union and confirmed during your discovery call.",
  },
];

export const OFFER_ITEMS = [
  { bold: "No monthly payments during the term", rest: "with the Home Equity Agreement option \u2014 settle when you sell, refinance, or at year 10" },
  { bold: "Two ways to fund it", rest: "\u2014 a Home Equity Agreement or a conventional credit union solar program" },
  { bold: "Qualify on equity, not income", rest: "\u2014 minimum ~500 FICO and no income requirement on the HEA option" },
  { bold: "Professional installation", rest: "by Maui-licensed electricians" },
  { bold: "24/7 system monitoring", rest: "\u2014 we watch it so you don\u2019t have to" },
  { bold: "All maintenance and repairs covered", rest: "\u2014 no surprise bills, ever" },
  { bold: "Industry-leading warranties", rest: "\u2014 a 6-year workmanship warranty plus a 15-year battery warranty, the longest in the industry" },
  { bold: "Battery backup options available", rest: "\u2014 EcoFlow OCEAN Pro and Sigenergy systems for island resilience" },
  { bold: "You keep full title and ownership", rest: "of your home throughout the program" },
];

// Premium battery / equipment showcase. Specs per manufacturer datasheets.
export const EQUIPMENT = [
  {
    name: "EcoFlow OCEAN Pro",
    tag: "Whole-Home Backup",
    image: "/images/product-ecoflow-ocean-pro.jpg",
    points: [
      "Scalable storage from 10 kWh up to 80 kWh per system",
      "Up to 24 kW continuous output \u2014 run AC, pumps, and heavy appliances",
      "Safe, long-life LFP batteries with advanced battery management",
      "Backed by a 15-year manufacturer warranty",
    ],
  },
  {
    name: "Sigenergy SigenStor",
    tag: "5-in-One AI System",
    image: "/images/product-sigenergy-alt.png",
    contain: true,
    points: [
      "5-in-1: solar inverter, battery, PCS, EV DC charger, and EMS in one",
      "Modular, stackable design \u2014 scale your capacity on demand",
      "Up to 100% usable depth of discharge for maximum energy use",
      "IP66-rated and AI-optimized for smarter savings",
    ],
  },
];

// Real Maui install photos (homeowner-supplied).
export const GALLERY = [
  { src: "/images/install-roof-ocean.jpg", caption: "Rooftop solar with an ocean view \u2014 East Maui", span: true },
  { src: "/images/install-batteries-outdoor-1.jpg", caption: "EcoFlow OCEAN Pro battery bank \u2014 covered outdoor install" },
  { src: "/images/install-batteries-garage-1.jpg", caption: "Clean in-garage battery backup" },
  { src: "/images/install-batteries-outdoor-2.jpg", caption: "Expandable storage for true island resilience" },
  { src: "/images/install-batteries-garage-2.jpg", caption: "Tidy, code-compliant installation" },
];

// Featured authority endorsement (Google review).
export const LANE_REVIEW = {
  name: "Lane Myers",
  title: "Luxury Home Builder \u2014 Lane Myers Custom Homes, Utah",
  meta: "West Maui Homeowner \u00b7 40-Year Contractor \u00b7 Google Local Guide",
  website: "https://lanemyers.com",
  websiteLabel: "lanemyers.com",
  rating: 5,
  quote:
    "Like all of the other reviews on Maui StarPower \u2014 I too am way impressed. I\u2019ve been a contractor for 40 years and have high expectations. Unfortunately, I\u2019m typically really disappointed with island service: most just don\u2019t care, most don\u2019t answer the phone, and most won\u2019t return your call. Ahmad is like the service I\u2019m used to. I can highly recommend him.",
  ownerReply:
    "Thank you Lane! We are so happy you appreciate the work we did on your beautiful home. Enjoy your battery backup system through all the power outages. Stay cool! \uD83D\uDE0E",
};

export const TESTIMONIALS = [
  {
    feature: true,
    quote:
      "2 weeks from our first meeting, I had a fully functioning solar system installed in my home. Ahmad is wonderful to work with, and the price point is more than reasonable. Mahalo nui STARPOWER!!!!",
    name: "Morgan Hawkins",
    source: "Google Review",
    rating: 5,
  },
  {
    quote:
      "The price value you get for the quality of equipment plus installation is the best available on Maui today \u2014 I did extensive research. Ahmad is fantastic, friendly and knowledgeable. Mahalo to Ahmad and their team.",
    name: "Mauiquetzal, 13moons Farm Homestead, Maui",
    source: "Google Review",
    rating: 5,
  },
  {
    quote:
      "Ahmad was professional, knowledgeable, and committed to making sure everything was done right. It\u2019s rare to find someone who delivers excellent work AND takes the time to educate and support their customers. You\u2019ll be in great hands.",
    name: "Erika Thompson",
    source: "Google Review",
    rating: 5,
  },
  {
    quote:
      "From the consult to the installation, we couldn\u2019t have asked for a better team. Professional, trustworthy, and went above and beyond. If you\u2019re looking for solar on Maui \u2014 look no further. These guys are the best.",
    name: "Shannon Oberg",
    source: "Recommends StarPower.Network on Facebook",
    recommends: true,
  },
];

export const FAQS = [
  {
    q: "Is this a loan?",
    a: "Not necessarily. Our flagship option is a Home Equity Agreement (HEA) \u2014 that\u2019s not a loan, HELOC, or refinance. You receive cash from your home\u2019s equity to fund solar, make no monthly payments during the term, and settle when you sell, refinance, or at year 10. Prefer a traditional path? We also offer conventional credit union solar loan programs. Your discovery call helps you choose the right fit.",
  },
  {
    q: "What does it cost?",
    a: "With the Home Equity Agreement there\u2019s an origination fee plus standard third-party closing costs (deducted from your proceeds \u2014 you don\u2019t write a check), and at settlement the financing partner receives an agreed share of your home\u2019s value, capped by a 19.9% annualized cost limit. Conventional credit union programs use standard loan rates and terms instead. We\u2019ll walk through real numbers for your situation on the call. (Illustrative only \u2014 your offer may vary.)",
  },
  {
    q: "Do I need great credit or income?",
    a: "The Home Equity Agreement qualifies you on your equity, not your paycheck \u2014 minimum around a 500 FICO and no income requirement. That makes it a strong fit for retirees, self-employed homeowners, and anyone a bank has turned down. Conventional credit union programs have their own credit guidelines we\u2019ll review with you.",
  },
  {
    q: "What happens to my HECO bill?",
    a: "It can drop dramatically. Most of your power comes from solar; any remaining grid usage still comes from HECO. Many Maui homeowners see a large reduction in their total electricity costs. Actual savings vary by property and usage.",
  },
  {
    q: "How fast is installation?",
    a: "Fast. One of our customers went from first meeting to fully installed system in 2 weeks. Timelines depend on permitting, but we handle all of it \u2014 Maui County permitting, HECO interconnection, inspections. All included.",
  },
  {
    q: "Do I give up ownership of my home?",
    a: "No. With the Home Equity Agreement you keep full title, live in your home as always, and keep your property tax benefits. The financing partner simply records a second-position lien and is settled later when you sell, refinance, or buy out \u2014 up to 10 years.",
  },
  {
    q: "What if I sell my home?",
    a: "With the Home Equity Agreement, you settle the agreement at sale by sharing in your home\u2019s value. There\u2019s even an improvement adjustment, so the value your solar adds isn\u2019t fully counted against you at settlement. With a credit union loan, the balance is typically paid from the sale. We\u2019ll explain both clearly.",
  },
  {
    q: "What about power outages?",
    a: "Standard grid-tied solar goes offline during outages (a HECO safety requirement). If you want power during outages, ask about our battery backup options \u2014 EcoFlow OCEAN Pro and Sigenergy SigenStor systems available. Bring it up on your call.",
  },
  {
    q: "Who handles maintenance?",
    a: "We do. Maintenance and repairs are covered, and we monitor your system 24/7. If something needs attention, we schedule it. You don\u2019t lift a finger.",
  },
  {
    q: "Is my roof suitable?",
    a: "We assess your roof using satellite imagery before your call \u2014 so we already have a design when we talk. Most Maui homes qualify. Your discovery call confirms everything for your specific property.",
  },
];

export const ABOUT_BADGES = [
  { icon: "island", label: "Maui-Based & Operated" },
  { icon: "bolt", label: "Licensed Electrical Contractor \u2014 Maui County" },
  { icon: "shield", label: "Fully Insured" },
  { icon: "signal", label: "HECO Interconnection Specialists" },
  { icon: "star", label: "5-Star Google Rated" },
  { icon: "wrench", label: "6-Year Workmanship Warranty" },
  { icon: "battery", label: "15-Year Battery Warranty \u2014 Longest in the Industry" },
];

export const DISCLAIMER =
  "Solar savings estimates vary by property, usage, and program eligibility and are not guaranteed. The Home Equity Agreement (HEA) is offered through a third-party financing partner (Unlock Technologies, based in Tempe, AZ; available in Hawaii) and is not a loan, mortgage, HELOC, or refinance. An origination fee, third-party closing costs, and an agreed share of your home\u2019s future value apply at settlement, subject to a 19.9% annualized cost limit. Conventional credit union loan programs are offered through participating local credit unions under their own terms. Product specifications (EcoFlow OCEAN Pro, Sigenergy SigenStor) are per manufacturer datasheets and subject to change. All figures shown are illustrative; your offer may vary. Maui StarPower Network is a solar and electrical contractor and does not provide financing directly.";
